import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  Search,
  ArrowRight,
  Clock,
  CheckCircle,
  Heart,
  Calculator,
  MapPin,
  Layers,
  UserCheck,
  RefreshCw,
  Target
} from "lucide-react";
import { Header, Footer } from "../../../../components/Layout";

const IntegrativoPage = () => {
  const principles = [
    {
      title: "Personalización total",
      description: "No existe un tratamiento único para todos. Cada persona recibe un plan diseñado específicamente para sus necesidades.",
      icon: UserCheck
    },
    {
      title: "Múltiples modelos",
      description: "Combina técnicas de diferentes enfoques: cognitivo-conductual, humanista, sistémico, psicodinámico y otros.",
      icon: Layers
    },
    {
      title: "Flexibilidad",
      description: "El tratamiento se adapta continuamente según la respuesta y evolución de cada paciente.",
      icon: RefreshCw
    },
    {
      title: "Orientado a objetivos",
      description: "Define metas claras y ajusta las intervenciones para alcanzarlas de la forma más efectiva.",
      icon: Target
    }
  ];

  const approaches = [
    { name: "Cognitivo-Conductual", use: "Base del tratamiento, prevención de recaídas" },
    { name: "Humanista", use: "Motivación, autoestima, sentido de vida" },
    { name: "Sistémico", use: "Trabajo con familia y relaciones" },
    { name: "Psicodinámico", use: "Comprensión de causas profundas" },
    { name: "Gestalt", use: "Trabajo emocional, aquí y ahora" },
    { name: "EMDR", use: "Procesamiento de traumas" },
    { name: "Mindfulness", use: "Regulación emocional, conciencia" },
    { name: "12 Pasos", use: "Comunidad y espiritualidad" }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalTherapy",
    "name": "Tratamiento Integrativo para Adicciones",
    "description": "Enfoque personalizado que combina múltiples modelos terapéuticos adaptados a cada persona."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Tratamientos", "item": "https://sinadicciones.org/tratamientos" },
      { "@type": "ListItem", "position": 3, "name": "Residencial", "item": "https://sinadicciones.org/tratamiento/residencial" },
      { "@type": "ListItem", "position": 4, "name": "Integrativo", "item": "https://sinadicciones.org/tratamiento/residencial/integrativo" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Tratamiento Integrativo para Adicciones: Enfoque Personalizado | Sin Adicciones Chile</title>
        <meta name="description" content="Tratamiento integrativo residencial para adicciones. Combina múltiples enfoques terapéuticos adaptados a cada persona: TCC, humanista, sistémico y más." />
        <link rel="canonical" href="https://sinadicciones.org/tratamiento/residencial/integrativo" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/tratamiento/residencial/integrativo" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400 flex-wrap">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamientos" className="hover:text-emerald-400 transition-colors">Tratamientos</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/tratamiento/residencial" className="hover:text-emerald-400 transition-colors">Residencial</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Integrativo</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-pink-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-pink-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-pink-500/20 flex items-center justify-center">
                <Heart className="w-7 h-7 text-pink-400" />
              </div>
              <div className="px-4 py-2 bg-pink-500/10 border border-pink-500/20 rounded-full">
                <span className="text-pink-400 font-medium">Personalizado</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Enfoque{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-rose-400">
                Integrativo
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              Lo mejor de todos los mundos. Un tratamiento diseñado específicamente 
              para ti, combinando las técnicas más efectivas de múltiples enfoques 
              terapéuticos.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Layers className="w-5 h-5 text-pink-400 mb-2" />
                <p className="text-white font-bold">8+ enfoques</p>
                <p className="text-slate-400 text-sm">combinados</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <Clock className="w-5 h-5 text-pink-400 mb-2" />
                <p className="text-white font-bold">3-12 meses</p>
                <p className="text-slate-400 text-sm">según necesidad</p>
              </div>
              <div className="p-4 bg-slate-800/50 rounded-xl border border-slate-700/50">
                <UserCheck className="w-5 h-5 text-pink-400 mb-2" />
                <p className="text-white font-bold">100%</p>
                <p className="text-slate-400 text-sm">personalizado</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile?approach=integrativo"
                className="px-6 py-3 bg-pink-500 hover:bg-pink-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Centros con Enfoque Integrativo
              </Link>
              <Link 
                to="/calculadora"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Calculator className="w-5 h-5" />
                Calcular Costo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Principios del Enfoque Integrativo
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Un tratamiento tan único como tú.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {principles.map((principle, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-pink-500/20"
              >
                <div className="w-12 h-12 rounded-xl bg-pink-500/20 flex items-center justify-center mb-4">
                  <principle.icon className="w-6 h-6 text-pink-400" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{principle.title}</h3>
                <p className="text-slate-400">{principle.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approaches Used */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Enfoques que se Integran
            </h2>
            <p className="text-slate-400">
              Cada enfoque aporta herramientas específicas que se combinan según tus necesidades.
            </p>
          </div>

          <div className="space-y-4">
            {approaches.map((approach, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50"
              >
                <CheckCircle className="w-5 h-5 text-pink-400 flex-shrink-0" />
                <div className="flex-1">
                  <span className="text-white font-medium">{approach.name}</span>
                  <span className="text-slate-500 mx-2">→</span>
                  <span className="text-slate-400">{approach.use}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              ¿Cómo funciona?
            </h2>
          </div>

          <div className="space-y-4">
            {[
              { step: "1", title: "Evaluación completa", description: "Diagnóstico integral que evalúa todas las dimensiones de tu situación" },
              { step: "2", title: "Plan personalizado", description: "Se diseña un programa que combina los enfoques más adecuados para ti" },
              { step: "3", title: "Implementación flexible", description: "El tratamiento se ajusta según tu respuesta y evolución" },
              { step: "4", title: "Revisión continua", description: "Evaluaciones periódicas para optimizar el plan de tratamiento" }
            ].map((item, index) => (
              <div 
                key={index}
                className="flex items-start gap-4 p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50"
              >
                <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-pink-400 font-bold">{item.step}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-slate-400">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-pink-900/50 to-slate-800/50 border border-pink-500/20 p-8 md:p-12 text-center">
            <Heart className="w-16 h-16 text-pink-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Encuentra Centros con Enfoque Integrativo
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Busca centros que ofrezcan tratamiento personalizado con múltiples enfoques.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/centros-de-rehabilitacion-drogas-chile"
                className="px-8 py-4 bg-pink-500 hover:bg-pink-600 text-white font-bold rounded-xl transition-all flex items-center gap-2"
              >
                <MapPin className="w-5 h-5" />
                Ver Centros
              </Link>
              <Link 
                to="/tratamiento/residencial"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <ArrowRight className="w-5 h-5" />
                Otros Enfoques
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Otros Enfoques Terapéuticos</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Cognitivo", path: "/tratamiento/residencial/cognitivo", color: "text-purple-400" },
              { title: "Conductual", path: "/tratamiento/residencial/conductual", color: "text-orange-400" },
              { title: "Cognitivo-Conductual", path: "/tratamiento/residencial/cognitivo-conductual", color: "text-emerald-400" },
              { title: "Holístico", path: "/tratamiento/residencial/holistico", color: "text-green-400" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-pink-500/30 transition-all group"
              >
                <span className={`font-medium ${item.color}`}>{item.title}</span>
                <ArrowRight className="w-4 h-4 text-slate-500 ml-auto group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default IntegrativoPage;
